import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import Box from 'carbon-react/lib/components/box';
import Pill from 'carbon-react/lib/components/pill';
import Typography from 'carbon-react/lib/components/typography';

import { ListingPageRowLayout } from '@futrli/ui-components/components/listing-page/listing-page-row/listing-page-row-layout/listing-page-row-layout';
import { ListingPageRowMetricCell } from '@futrli/ui-components/components/listing-page/listing-page-row/listing-page-row-metric-cell/listing-page-row-metric-cell';
import { translateUserRole } from '@futrli/backend-messages/common/user-roles';
import { ProcessedUserRow } from '@futrli/views/processors/users/usersList';

import { UserRowActions } from '../user-row-actions/user-row-actions';

export const ROW_HEIGHT_DEFAULT = 78;

interface UserRowProps {
  user: ProcessedUserRow;
}

export const UserRow = ({ user }: UserRowProps) => {
  const intl = useIntl();
  const isInvitationPending = user.status === 'pending';
  const hasActions = user.actions.delete || user.actions.edit;
  const statusLabel = isInvitationPending
    ? intl.formatMessage({ id: 'common.invitation_sent', defaultMessage: 'Invitation sent' })
    : intl.formatMessage({ id: 'common.active', defaultMessage: 'Active' });

  return (
    <ListingPageRowLayout
      nameColumn={
        <Box display="flex" flexDirection="column">
          <Typography variant="span" fontWeight="500">
            {user.displayName}
          </Typography>
          <Typography variant="span" fontSize="12px" color="--colorsUtilityYin055">
            {user.email_address}
          </Typography>
        </Box>
      }
      metrics={
        <>
          <ListingPageRowMetricCell
            label={<FormattedMessage id="common.role" defaultMessage="Role" />}
            size="statusLarge"
            value={translateUserRole(user.role.name, intl)}
          />
          <ListingPageRowMetricCell
            label={
              <FormattedMessage id="predict.workspaces.users.organisation_count" defaultMessage="Organisation count" />
            }
            value={intl.formatNumber(user.organisationCount)}
            size="status"
          />
          <ListingPageRowMetricCell
            label={<FormattedMessage id="common.status" defaultMessage="Status" />}
            value={
              <Pill colorVariant={isInvitationPending ? undefined : 'positive'} pillRole="status" size="S" fill>
                {statusLabel}
              </Pill>
            }
            size="status"
          />
          <ListingPageRowMetricCell
            label={<FormattedMessage id="predict.workspaces.users.mfa_enabled" defaultMessage="MFA enabled" />}
            value={
              user.isMfaEnabled ? (
                <FormattedMessage id="predict.yes" defaultMessage="Yes" />
              ) : (
                <FormattedMessage id="predict.no" defaultMessage="No" />
              )
            }
            size="status"
          />
        </>
      }
      actions={hasActions ? <UserRowActions user={user} /> : undefined}
    />
  );
};
